/**
 * Pure view-model builders for the «Обзор» agenda feed.
 *
 * No React, no i18n, no styling — every function maps calendar days into the
 * agenda entry list so the split / ordering / gap-collapsing rules are unit
 * testable in isolation (see `__tests__/agendaModel.test.ts`).
 */
import { TCalendarDay, TCalendarRow, INCOME_STATE } from '../shared';

/** A rendered agenda day: the day bucket + its two pre-sorted columns. */
export interface IAgendaDayEntry {
  type: 'day';
  /** ISO date of the day ('YYYY-MM-DD'). */
  date: string;
  /** The backing day bucket (totals / tone / balances). */
  day: TCalendarDay;
  /** «Придёт» — income rows: overdue → awaited → expected, received last. */
  incoming: TCalendarRow[];
  /** «Уйдёт» — the day's expense rows (backend order preserved). */
  outgoing: TCalendarRow[];
}

/** A run of consecutive days with no movements, collapsed into one row. */
export interface IAgendaGapEntry {
  type: 'gap';
  /** ISO date of the first quiet day. */
  from: string;
  /** ISO date of the last quiet day (equals `from` for a single day). */
  to: string;
}

export type TAgendaEntry = IAgendaDayEntry | IAgendaGapEntry;

/** Nullable-list → concrete rows. */
export const compactRows = (
  list: ReadonlyArray<TCalendarRow | null> | null | undefined,
): TCalendarRow[] => (list || []).filter(Boolean) as TCalendarRow[];

/** Sort rank inside «Придёт»: actionable first, received last. */
const INCOME_RANK: Record<string, number> = {
  [INCOME_STATE.overdue]: 0,
  [INCOME_STATE.awaited]: 1,
  [INCOME_STATE.expected]: 2,
  [INCOME_STATE.received]: 3,
};

const incomeRank = (row: TCalendarRow): number =>
  INCOME_RANK[row.incomeState || ''] ?? 2;

/**
 * Split one day into the two agenda columns.
 *
 *  - `incoming` = the day's income rows sorted overdue → awaited → expected
 *    (stable within a rank), with the already-received rows appended at the
 *    end (they render with the «получено» chip).
 *  - `outgoing` = the day's expense rows as-is.
 */
export const splitDayRows = (
  day: TCalendarDay,
): { incoming: TCalendarRow[]; outgoing: TCalendarRow[] } => {
  const planned = compactRows(day.incomeRows)
    .map((row, index) => ({ row, index }))
    .sort((a, b) => incomeRank(a.row) - incomeRank(b.row) || a.index - b.index)
    .map(({ row }) => row);
  return {
    incoming: [...planned, ...compactRows(day.receivedRows)],
    outgoing: compactRows(day.expenseRows),
  };
};

/** True when the day has no rows in either agenda column. */
export const isQuietDay = (day: TCalendarDay): boolean =>
  compactRows(day.incomeRows).length === 0 &&
  compactRows(day.receivedRows).length === 0 &&
  compactRows(day.expenseRows).length === 0;

/**
 * Build the agenda feed: one `day` entry per day with movements (columns
 * pre-split and sorted), with every run of consecutive quiet days collapsed
 * into a single `gap` entry («5–7 июля — нет движений»). Days are processed
 * in ascending date order; undated buckets are skipped.
 */
export const buildAgenda = (days: TCalendarDay[]): TAgendaEntry[] => {
  const dated = days
    .filter((d): d is TCalendarDay & { date: string } => !!d.date)
    .sort((a, b) => a.date.localeCompare(b.date));

  const entries: TAgendaEntry[] = [];
  dated.forEach((day) => {
    if (isQuietDay(day)) {
      const last = entries[entries.length - 1];
      if (last && last.type === 'gap') {
        last.to = day.date;
      } else {
        entries.push({ type: 'gap', from: day.date, to: day.date });
      }
      return;
    }
    const { incoming, outgoing } = splitDayRows(day);
    entries.push({ type: 'day', date: day.date, day, incoming, outgoing });
  });
  return entries;
};
